/**
 * Agent transaction context: payload the system sets when an agent (or underwriter)
 * starts a purchase-cover transaction so that the business is linked correctly.
 * Used in reserve-cover, save-application, and payment (STK) requests.
 */

export const AGENT_TRANSACTION_CONTEXT_KEY = 'agentbima_agent_transaction_context';

/**
 * Build the agent transaction context payload.
 * @param {Object} options
 * @param {string|number} [options.agentId] - Agent ID linking the transaction to the agent
 * @param {Object} [options.agency] - Agency details { shortDescription?, taxPin? }
 * @param {string|number} [options.agentUserId] - Optional agent_user ID for employee transactions
 * @param {number} [options.issuedByAdminId] - Underwriter admin user ID (Covers Issued list)
 * @param {boolean} [options.isDirectBusiness] - Underwriter direct business (no agent, no commission)
 * @returns {Object|null} Payload for API requests
 */
export function buildAgentTransactionContext({
  agentId,
  agency,
  agentUserId,
  issuedByAdminId,
  isDirectBusiness = false
} = {}) {
  const isDirect = isDirectBusiness === true;
  const hasAgent = !isDirect && agentId != null && String(agentId).trim() !== '';
  const hasAgency = !isDirect && agency && typeof agency === 'object' && (agency.shortDescription || agency.taxPin);
  const hasIssuedByAdmin = issuedByAdminId != null && !isNaN(parseInt(issuedByAdminId, 10));
  if (!hasAgent && !hasAgency && !hasIssuedByAdmin && !isDirect) return null;

  return {
    source: 'AGENTBIMA',
    transactionStartedAt: new Date().toISOString(),
    ...(isDirect
      ? {
        isDirectBusiness: true,
        commissionAllowed: 'No',
        agency: { shortDescription: '', taxPin: '' }
      }
      : {}),
    ...(hasAgent ? { agentId } : {}),
    ...(hasAgency ? { agency } : {}),
    ...(agentUserId != null && String(agentUserId).trim() !== '' ? { agentUserId } : {}),
    ...(hasIssuedByAdmin ? { issuedByAdminId: parseInt(issuedByAdminId, 10) } : {})
  };
}

/**
 * Persist agent transaction context to sessionStorage so it survives refresh.
 * @param {Object} context - Result of buildAgentTransactionContext()
 */
export function persistAgentTransactionContext(context) {
  if (typeof sessionStorage === 'undefined' || !context) return;
  try {
    sessionStorage.setItem(AGENT_TRANSACTION_CONTEXT_KEY, JSON.stringify(context));
  } catch (e) {
    console.warn('Could not persist agent transaction context:', e.message);
  }
}

/**
 * Read agent transaction context from sessionStorage (e.g. after refresh).
 * @returns {Object|null}
 */
export function getPersistedAgentTransactionContext() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AGENT_TRANSACTION_CONTEXT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Merge agent transaction context into a request body.
 * @param {Object} body - Existing request body
 * @param {Object|null} context - Agent transaction context
 * @returns {Object} body with context fields merged in
 */
export function mergeAgentContextIntoRequestBody(body, context) {
  if (!context || typeof body !== 'object') return body;
  const isDirect = context.isDirectBusiness === true;
  return {
    ...body,
    ...(isDirect
      ? {
        isDirectBusiness: true,
        commissionAllowed: 'No',
        agency: { shortDescription: '', taxPin: '' }
      }
      : {}),
    ...(!isDirect && context.agentId != null ? { agentId: context.agentId } : {}),
    ...(!isDirect && context.agency ? { agency: context.agency } : {}),
    ...(context.agentUserId != null ? { agentUserId: context.agentUserId } : {}),
    ...(context.issuedByAdminId != null ? { issuedByAdminId: context.issuedByAdminId } : {}),
    ...(context.commissionAllowed != null && !body.commissionAllowed ? { commissionAllowed: context.commissionAllowed } : {})
  };
}

/**
 * Clear persisted context (e.g. when purchase is completed or flow is closed).
 */
export function clearAgentTransactionContext() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(AGENT_TRANSACTION_CONTEXT_KEY);
  } catch (e) {}
}
